import { Categories } from "lib/constants";
import signupBlockResolver, { ResolvedSignUpBlock } from "models/signupBlockResolver";
import type {
    AccordionGroup,
    AddedProtectionSection,
    CustomTable,
    FAQ,
    Feature,
    HeroTextImage,
    IconList,
    Image,
    InlineFrame,
    Quotation,
    ReviewsContent,
    ReviewsService,
    SectionFeaturedFigures,
    SectionIconList,
    SectionImageGallery,
    Shopfront,
    SponsorshipCard,
    StaticPageSubscribe,
    TextMediaBlock,
    Timeline,
    Video,
} from "services/cms/models";
import { ContentTypeCodenames } from "services/cms/models/contentTypeCodenames";
import { CookieSettingsWidget } from "services/cms/models/cookieSettingsWidget";
import { CopyOnly } from "services/cms/models/copyOnly";
import { CustomSignUpBlock } from "services/cms/models/customSignUpBlock";
import { DynamicPhoneNumber } from "services/cms/models/dynamicPhoneNumber";
import { EventBlock } from "services/cms/models/eventBlock";
import { MediaContentTable } from "services/cms/models/mediaContentTable";
import { PageAnchor } from "services/cms/models/pageAnchor";
import { RelatedArticles } from "services/cms/models/relatedArticles";
import { SignUpBlock } from "services/cms/models/signUpBlock";
import { StaticCardList } from "services/cms/models/staticCardList";
import { StaticPageComponentWrapper } from "services/cms/models/staticPageComponentWrapper";
import { Testimonials } from "services/cms/models/testimonials";
import type {
    IContentItem,
    IContentItemElements,
    IContentItemsContainer,
} from "services/content-manager/models";

import accordionGroupResolver from "./accordionGroupResolver";
import type { ResolvedAccordionGroup } from "./accordionGroupResolver";
import type { ResolvedAddedProtectionSection } from "./addedProtectionSectionResolver";
import addedProtectionSectionResolver from "./addedProtectionSectionResolver";
import cookieSettingsWidgetResolver, {
    ResolvedCookieSettingsWidget,
} from "./cookieSettingsWidgetResolver";
import copyOnlyResolver, { ResolvedCopyOnly } from "./copyOnlyResolver";
import customSignupBlockResolver, { ResolvedCustomSignUpBlock } from "./customSignUpBlockResolver";
import customTableResolver, { ResolvedCustomTable } from "./customTableResolver";
import dynamicPhoneNumberResolver, {
    ResolvedDynamicPhoneNumber,
} from "./dynamicPhoneNumberResolver";
import type { ResovledEventBlock } from "./eventBlockResovler";
import eventBlockResovler from "./eventBlockResovler";
import type { ResolvedFAQ } from "./faqResolver";
import faqResolver from "./faqResolver";
import type { ResolvedFeature } from "./featureResolver";
import featureResolver from "./featureResolver";
import type { ResolvedFeaturedFigures } from "./featuredFiguresResolver";
import featuredFiguresResolver from "./featuredFiguresResolver";
import type { ResolvedheroTextImage } from "./heroTextImageResolver";
import heroTextImageResolver from "./heroTextImageResolver";
import type { ResolvedIconList } from "./iconListResolver";
import iconListResolver from "./iconListResolver";
import type { ResolvedIconUl } from "./iconUlResolver";
import iconUlResolver from "./iconUlResolver";
import type { ResolvedImageGallery } from "./imageGalleryResolver";
import imageGalleryResolver from "./imageGalleryResolver";
import type { ResolvedImage } from "./imageResolver";
import imageResolver from "./imageResolver";
import type { ResolvedInlineFrame } from "./inlineFrameResolver";
import inlineFrameResolver from "./inlineFrameResolver";
import mediaContentTableResolver, { ResolvedMediaContentTable } from "./mediaContentTableResolver";
import pageAnchorResolver, { ResolvedPageAnchor } from "./pageAnchorResolver";
import quotationResolver from "./quotationResolver";
import type { ResolvedQuotation } from "./quotationResolver";
import relatedArticlesResolver, { ResolvedRelatedArticles } from "./relatedArticlesResolver";
import type { ResolvedReviewsContent } from "./reviewsContentResolver";
import reviewsContentResolver from "./reviewsContentResolver";
import type { ResolvedReviewsService } from "./reviewsServiceResolver";
import reviewsServiceResolver from "./reviewsServiceResolver";
import shopfrontResolver, { ResolvedShopfront } from "./shopfrontResolver";
import type { ResolvedSponsorshipCard } from "./sponsorshipCardResolver";
import sponsorshipCardResolver from "./sponsorshipCardResolver";
import staticCardListResolver, { ResolvedStaticCardList } from "./staticCardListResolver";
import staticPageComponentWrapperResolver, {
    ResolvedStaticPageComponentWrapper,
} from "./staticPageComponentWrapperResolver";
import type { ResolvedStaticPageSubscribe } from "./staticPageSubscribeResolver";
import staticPageSubscribeResolver from "./staticPageSubscribeResolver";
import testimonialsResolver, { ResolvedTestimonials } from "./testimonialsResolver";
import type { ResovledTextMediaBlock } from "./textMediaBlockResovler";
import textMediaBlockResovler from "./textMediaBlockResovler";
import type { ResolvedTimeline } from "./timelineResolver";
import timelineResolver from "./timelineResolver";
import type { ResolvedVideo } from "./videoResolver";
import videoResolver from "./videoResolver";

export interface ResolvedRichText {
    html: string;
    resolvedItems?: ResolvedItem[];
}

export type ResolvedItem =
    | ResolvedFeaturedFigures
    | ResolvedIconList
    | ResolvedImage
    | ResolvedImageGallery
    | ResolvedVideo
    | ResolvedReviewsContent
    | ResolvedTimeline
    | ResolvedSponsorshipCard
    | ResolvedReviewsService
    | ResolvedFeature
    | ResolvedFAQ
    | ResolvedInlineFrame
    | ResolvedIconUl
    | ResovledTextMediaBlock
    | ResolvedheroTextImage
    | ResovledEventBlock
    | ResolvedTestimonials
    | ResolvedStaticPageSubscribe
    | ResolvedAddedProtectionSection
    | ResolvedCustomTable
    | ResolvedAccordionGroup
    | ResolvedQuotation
    | ResolvedShopfront
    | ResolvedCookieSettingsWidget
    | ResolvedPageAnchor
    | ResolvedSignUpBlock
    | ResolvedStaticCardList
    | ResolvedRelatedArticles
    | ResolvedDynamicPhoneNumber
    | ResolvedStaticPageComponentWrapper
    | ResolvedMediaContentTable
    | ResolvedCopyOnly
    | ResolvedCustomSignUpBlock;

const getResolvedItem = (item: IContentItem): ResolvedItem | null => {
    switch (item.system.type) {
        case ContentTypeCodenames.CookieSettingsWidget:
            return cookieSettingsWidgetResolver(item as CookieSettingsWidget);

        case ContentTypeCodenames.Image:
            return imageResolver(item as Image);

        case ContentTypeCodenames.SectionFeaturedFigures:
            return featuredFiguresResolver(item as SectionFeaturedFigures);

        case ContentTypeCodenames.SectionIconList:
            return iconListResolver(item as SectionIconList);

        case ContentTypeCodenames.Video:
            return videoResolver(item as Video);

        case ContentTypeCodenames.SectionImageGallery:
            return imageGalleryResolver(item as SectionImageGallery);

        case ContentTypeCodenames.ReviewsContent:
            return reviewsContentResolver(item as ReviewsContent);

        case ContentTypeCodenames.AboutUsTimeline:
            return timelineResolver(item as Timeline);

        case ContentTypeCodenames.SponsorshipCard:
            return sponsorshipCardResolver(item as SponsorshipCard);

        case ContentTypeCodenames.ReviewsService:
            return reviewsServiceResolver(item as ReviewsService);

        case ContentTypeCodenames.Feature:
            return featureResolver(item as Feature);

        case ContentTypeCodenames.FAQ:
            return faqResolver(item as FAQ);

        case ContentTypeCodenames.InlineFrame:
            return inlineFrameResolver(item as InlineFrame);

        case ContentTypeCodenames.IconList:
            return iconUlResolver(item as IconList);

        case ContentTypeCodenames.TextMediaBlock:
            return textMediaBlockResovler(item as TextMediaBlock);

        case ContentTypeCodenames.HeroTextImage:
            return heroTextImageResolver(item as HeroTextImage);

        case ContentTypeCodenames.EventBlock:
            return eventBlockResovler(item as EventBlock);

        case ContentTypeCodenames.Testimonials:
            return testimonialsResolver(item as Testimonials);

        case ContentTypeCodenames.StaticPageSubscribe:
            return staticPageSubscribeResolver(item as StaticPageSubscribe);

        case ContentTypeCodenames.AddedProtectionSection:
            return addedProtectionSectionResolver(item as AddedProtectionSection);

        case ContentTypeCodenames.CustomTable:
            return customTableResolver(item as CustomTable);

        case ContentTypeCodenames.AccordionGroup:
            return accordionGroupResolver(item as AccordionGroup);

        case ContentTypeCodenames.Quotation:
            return quotationResolver(item as Quotation);

        case ContentTypeCodenames.Shopfront:
            return shopfrontResolver(item as Shopfront);

        case ContentTypeCodenames.PageAnchor:
            return pageAnchorResolver(item as PageAnchor);

        case ContentTypeCodenames.SignUpBlock:
            return signupBlockResolver(item as SignUpBlock);

        case ContentTypeCodenames.CustomSignUpBlock:
            return customSignupBlockResolver(item as CustomSignUpBlock);

        case ContentTypeCodenames.StaticCardList:
            return staticCardListResolver(item as StaticCardList);

        case ContentTypeCodenames.RelatedArticles:
            return relatedArticlesResolver(item as RelatedArticles);

        case ContentTypeCodenames.DynamicPhoneNumber:
            return dynamicPhoneNumberResolver(item as DynamicPhoneNumber);

        case ContentTypeCodenames.MediaContentTable:
            return mediaContentTableResolver(item as MediaContentTable);

        case ContentTypeCodenames.StaticPageComponentWrapper:
            return staticPageComponentWrapperResolver(item as StaticPageComponentWrapper);

        case ContentTypeCodenames.CopyOnly:
            return copyOnlyResolver(item as CopyOnly);

        default: {
            return null;
        }
    }
};

type RichTextResolverProps = {
    html: string;
    linkedItems?: IContentItemsContainer | IContentItem<IContentItemElements>[];
    category?: Categories;
};

type RichTextResolver = (resolverProps: RichTextResolverProps) => ResolvedRichText;

const richTextResolver: RichTextResolver = ({ html, linkedItems, category }) => {
    if (!linkedItems) return { html };

    const resolvedItems = Object.values(linkedItems)
        .map((item) => getResolvedItem(item))
        .filter((value) => value) as ResolvedItem[];

    return {
        html,
        resolvedItems,
    };
};

export default richTextResolver;
